import Head from 'next/head';
import React from 'react';
import Script from 'next/script';
import { SessionProvider } from 'next-auth/react';
import { Layout } from 'antd';
import { prodLinks, CDN_IMAGES_URL } from 'util/constants';
import { ApolloProvider } from '@apollo/client';
import { useApollo } from '../src/apolloClient';
import { NavigationBar } from '@src/components/navigation-bar/NavigationBar';
import { CustomFooter } from '@ozoneuniverse/ui-components/build-pkg/src/components/footer/CustomFooter';
import type { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import { ConfigProvider } from 'antd';
import type { Session } from 'next-auth';
import { GeistSans } from 'geist/font/sans';
import NextTopLoader from 'nextjs-toploader';

const { Content } = Layout;

export const LocaleContext = React.createContext('');

export default function MyApp({
  Component,
  pageProps: { session, ...pageProps },
}: AppProps<{ session: Session }>) {
  const apolloClient = useApollo(pageProps);
  const router = useRouter();

  return (
    <>
      <Head>
        <meta charSet='utf-8' />
        <link rel='icon' href='/logo.jpeg' />
        <meta name='viewport' content='width=device-width, initial-scale=1' />
        <meta name='theme-color' content='#000000' />
        <link rel='apple-touch-icon' href='%PUBLIC_URL%/logo192.png' />
        <title>Ozone Metaverse Platform</title>
        <style global>{`
        html {
          height: 100%;
        }
        
        body {
          margin: 0px;
          height: 100%;
          font-family: ${GeistSans.style.fontFamily}, 'Inter', 'Helvetica Neue', Arial, sans-serif;
        }
        
        * {
          font-family: ${GeistSans.style.fontFamily}, 'Inter', 'Helvetica Neue', Arial, sans-serif;
        }
        
        body .layout {
          height: 100%;
          background-color: #090909;
        }
        
        .ant-flex {
          box-sizing: border-box;
        }
        
        .mail-unsub-success,
        .mail-unsub-error {
          margin-top: 90px;
          min-height: 40vh;
        }
        
        .layout .header {
          display: flex;
          align-items: center;
          gap: 0px;
          background: transparent;
          padding: 0px;
          z-index: 1001;
          position: sticky;
          position: -webkit-sticky; /* Safari */
          top: 0;
          margin-top: -82px;
        }
        
        .layout .footer {
          display: flex;
          padding: 0px;
          box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.15);
          overflow: hidden;
        }
        
        .content {
          box-sizing: border-box;
          width: 100%;
          min-height: 100vh;
          height: 100%;
          background-image: url('/bg_bubbles.svg');
          background-size: 100%;
          background-repeat: no-repeat;
          -o-object-fit: fill;
          object-fit: fill;
        }
        
        .content-root-wrapper {
          position: relative;
          display: block;
          max-width: 2560px;
          padding-right: 10vw;
          padding-left: 10vw;
          padding-bottom: 110px;
          margin-left: auto;
          margin-right: auto;
          background-color: #000000;
        }
        
        * img {
          object-fit: cover;
        }
        
        #user-profile-popover .ant-popover-inner-content {
          padding: 0px;
        }
        
        /* Change Autocomplete styles in Chrome*/
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active,
        textarea:-webkit-autofill,
        textarea:-webkit-autofill:hover,
        textarea:-webkit-autofill:focus,
        select:-webkit-autofill,
        select:-webkit-autofill:hover,
        select:-webkit-autofill:focus {
          border-radius: 1px;
        }
        input {
          color-scheme: dark;
        }
        
        @media only screen and (max-width: 932px) {
          .layout .footer {
            height: 100%;
          }
        }
        
        @media only screen and (max-width: 750px) {
          .content-root-wrapper {
            padding-left: 1vw;
            padding-right: 1vw;
          }
        }
        
        .yt-lite {
          background-color: #000;
          position: relative;
          display: block;
          contain: content;
          background-position: center center;
          background-size: cover;
          cursor: pointer;
          max-width: calc(77.778vh);
          max-height: 100%;
          width: 100%;
        }
        
        /* gradient */
        .yt-lite::before {
          content: '';
          display: block;
          position: absolute;
          top: 0;
          background-image: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAADGCAYAAAAT+OqFAAAAdklEQVQoz42QQQ7AIAgEF/T/D+kbq/RWAlnQyyazA4aoAB4FsBSA/bFjuF1EOL7VbrIrBuusmrt4ZZORfb6ehbWdnRHEIiITaEUKa5EJqUakRSaEYBJSCY2dEstQY7AuxahwXFrvZmWl2rh4JZ07z9dLtesfNj5q0FU3A5ObbwAAAABJRU5ErkJggg==);
          background-position: top;
          background-repeat: repeat-x;
          height: 60px;
          padding-bottom: 50px;
          width: 100%;
          transition: all 0.2s cubic-bezier(0, 0, 0.2, 1);
        }
        
        /* responsive iframe with a 16:9 aspect ratio
            thanks https://css-tricks.com/responsive-iframes/
        */
        .yt-lite::after {
          content: '';
          display: block;
          padding-bottom: calc(100% / (16 / 9));
        }
        .yt-lite > iframe {
          width: 100%;
          height: 100%;
          position: absolute;
          top: 0;
          left: 0;
        }
        
        /* play button */
        .yt-lite > .lty-playbtn {
          width: 70px;
          height: 46px;
          background-color: #212121;
          z-index: 1;
          opacity: 0.8;
          border-radius: 14%; /* TODO: Consider replacing this with YT's actual svg. Eh. */
          transition: all 0.2s cubic-bezier(0, 0, 0.2, 1);
        }
        .yt-lite:hover > .lty-playbtn {
          background-color: #f00;
          opacity: 1;
        }
        /* play button triangle */
        .yt-lite > .lty-playbtn:before {
          content: '';
          border-style: solid;
          border-width: 11px 0 11px 19px;
          border-color: transparent transparent transparent #fff;
        }
        
        .yt-lite > .lty-playbtn,
        .yt-lite > .lty-playbtn:before {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate3d(-50%, -50%, 0);
        }
        
        /* Post-click styles */
        .yt-lite.lyt-activated {
          cursor: unset;
        }
        .yt-lite.lyt-activated::before,
        .yt-lite.lyt-activated > .lty-playbtn {
          opacity: 0;
          pointer-events: none;
        }        
        `}</style>
      </Head>
      <Script
        src='https://www.googletagmanager.com/gtag/js?id=G-T3Q11QEEDJ'
        strategy='afterInteractive'
      />
      <Script id='google-analytics' strategy='afterInteractive'>
        {`
        window.dataLayer = window.dataLayer || [];
        function gtag(){window.dataLayer.push(arguments);}
        gtag('js', new Date());

        gtag('config', 'G-T3Q11QEEDJ');
      `}
      </Script>
      <style global>{`
        html {
          font-family: ${GeistSans.style.fontFamily}, 'Inter', 'Helvetica Neue', Arial, sans-serif;
        }
      `}</style>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: 'rgba(92, 54, 154, 1)',
            borderRadius: 12,
            fontSize: 14,
            colorHighlight: '#4a4ae5',
          },
          components: {
            Radio: {
              colorPrimary: '#8146DE',
              colorPrimaryHover: '#663da9',
            },
            Switch: {
              colorPrimary: '#87d068',
            },
            Tabs: {
              itemActiveColor: '#FFF',
              itemSelectedColor: '#FFF',
              inkBarColor: '#090909',
              colorPrimary: '#FFF',
              itemColor: '#939292',
              cardBg: '#090909',
              colorBgContainer: '#090909',
            },
            Typography: {
              fontWeightStrong: 700,
            },
            Drawer: {
              colorBgElevated: '#090909',
            },
          },
        }}
      >
        <ApolloProvider client={apolloClient}>
          <SessionProvider session={session}>
            <div className='layout'>
              <header className='header'>
                <NavigationBar></NavigationBar>
                <NextTopLoader
                  crawlSpeed={300}
                  crawl={true}
                  height={5}
                  showSpinner={false}
                  speed={300}
                />
              </header>
              <Content className='content'>
                <div className='content-root-wrapper'>
                  <LocaleContext.Provider value={router.locale ?? ''}>
                    <Component {...pageProps} />
                  </LocaleContext.Provider>
                </div>
              </Content>
              <div className='footer'>
                <CustomFooter
                  s3ImagesUrl={CDN_IMAGES_URL as string}
                  prodLinks={prodLinks}
                  type='default'
                />
              </div>
            </div>
          </SessionProvider>
        </ApolloProvider>
      </ConfigProvider>
    </>
  );
}
